import { SheetOverlay } from "./SheetOverlay";
import type { AccountView, BadgeView, SettingKey, SettingsView } from "./view-model";

export interface SettingsSheetProps {
  settings: SettingsView;
  reducedMotion: boolean;
  status: "no-pet" | "alive" | "dead";
  petName: string;
  account?: AccountView;
  /** Badges moved to the trophy case; accepted so callers need not change, and not shown here. */
  badges?: BadgeView[];
  usageCounts?: { on: boolean; onSet(on: boolean): void };
  onSetting<K extends SettingKey>(key: K, value: SettingsView[K]): void;
  onOpenRename(): void;
  onOpenAccount?(): void;
  onClose(): void;
}

export function SettingsSheet({
  settings,
  reducedMotion,
  status,
  petName,
  account,
  usageCounts,
  onSetting,
  onOpenRename,
  onOpenAccount = () => {},
  onClose,
}: SettingsSheetProps) {
  return (
    <SheetOverlay
      testId="settings-sheet"
      title="Settings"
      onClose={onClose}
    >
      <div className="sb">
        <h4>Play</h4>
        <div className="row col">
          <div className="grow">Controls</div>
          <div className="seg" role="group" aria-label="Controls">
            <button
              type="button"
              aria-pressed={settings.controls === "touch"}
              data-testid="setting-controls-touch"
              onClick={() => onSetting("controls", "touch")}
            >
              5 buttons
            </button>
            <button
              type="button"
              aria-pressed={settings.controls === "retro"}
              data-testid="setting-controls-retro"
              onClick={() => onSetting("controls", "retro")}
            >
              Retro 3 buttons
            </button>
          </div>
        </div>
        <div className="note">
          Retro mode: the left arrow button selects the previous care action, the middle check button
          does it, the right arrow button selects the next. In 5 button mode the shell buttons are
          hidden.
        </div>

        <div className="row col">
          <div className="grow">Meters shown as</div>
          <div className="seg" role="group" aria-label="Meters shown as">
            <button
              type="button"
              aria-pressed={settings.meterStyle === "bars"}
              data-testid="setting-meterStyle-bars"
              onClick={() => onSetting("meterStyle", "bars")}
            >
              Bars
            </button>
            <button
              type="button"
              aria-pressed={settings.meterStyle === "numbers"}
              data-testid="setting-meterStyle-numbers"
              onClick={() => onSetting("meterStyle", "numbers")}
            >
              Numbers
            </button>
          </div>
        </div>

        <div className="row col">
          <div className="grow">Meter labels</div>
          <div className="seg" role="group" aria-label="Meter labels">
            <button
              type="button"
              aria-pressed={settings.meterLabels === "icons"}
              data-testid="setting-meterLabels-icons"
              onClick={() => onSetting("meterLabels", "icons")}
            >
              Icons
            </button>
            <button
              type="button"
              aria-pressed={settings.meterLabels === "names"}
              data-testid="setting-meterLabels-names"
              onClick={() => onSetting("meterLabels", "names")}
            >
              Names
            </button>
          </div>
        </div>

        {status !== "no-pet" ? (
          <div className="row">
            <div className="grow">
              Pet name
              <small>{petName}</small>
            </div>
            <button
              type="button"
              className="btn"
              data-testid="setting-rename"
              onClick={onOpenRename}
            >
              Rename
            </button>
          </div>
        ) : null}

        <h4>Comfort</h4>
        <div className="row">
          <div className="grow">
            Sound effects
            <small>Small chirps for care, hatching and alerts</small>
          </div>
          <button
            className="sw"
            type="button"
            role="switch"
            aria-checked={settings.sound}
            aria-label="Sound effects"
            data-testid="setting-sound"
            onClick={() => onSetting("sound", !settings.sound)}
          />
        </div>

        {usageCounts ? (
          <div className="row">
            <div className="grow">
              Share anonymous usage counts
              <small>Only how often things happen, never who you are</small>
            </div>
            <button
              className="sw"
              type="button"
              role="switch"
              aria-checked={usageCounts.on}
              aria-label="Share anonymous usage counts"
              data-testid="setting-usageCounts"
              onClick={() => usageCounts.onSet(!usageCounts.on)}
            />
          </div>
        ) : null}

        <div className="row col">
          <div className="grow">
            Reduce motion
            <small>Stops the animation</small>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            {settings.reduceMotion !== null ? (
              <button
                type="button"
                className="btn alt"
                data-testid="setting-reduceMotion-device"
                onClick={() => onSetting("reduceMotion", null)}
              >
                Use device setting
              </button>
            ) : null}
            <button
              className="sw"
              type="button"
              role="switch"
              aria-checked={reducedMotion}
              aria-label="Reduce motion"
              data-testid="setting-reduceMotion"
              onClick={() => onSetting("reduceMotion", !reducedMotion)}
            />
          </div>
        </div>

        <div className="row">
          <div className="grow">Larger text</div>
          <button
            className="sw"
            type="button"
            role="switch"
            aria-checked={settings.largeText}
            aria-label="Larger text"
            data-testid="setting-largeText"
            onClick={() => onSetting("largeText", !settings.largeText)}
          />
        </div>

        <div className="row">
          <div className="grow">
            Notifications
            <small>Reminders will arrive with the mobile apps</small>
          </div>
          <button
            className="sw"
            type="button"
            role="switch"
            aria-checked={settings.notifications}
            aria-label="Notifications"
            data-testid="setting-notifications"
            onClick={() => onSetting("notifications", !settings.notifications)}
          />
        </div>

        <h4>Account</h4>
        <div className="row">
          <div className="grow">Parents’ area</div>
          <button className="btn alt" type="button" data-testid="setting-parents">
            Open
          </button>
        </div>
        <div className="row">
          <div className="grow">
            Account
            <small>{account?.signedIn && account.username ? account.username : "Not signed in"}</small>
          </div>
          <button
            className="btn alt"
            type="button"
            data-testid="setting-account"
            onClick={onOpenAccount}
          >
            Open
          </button>
        </div>
      </div>
    </SheetOverlay>
  );
}
